import { useState, useCallback, type ReactNode } from 'react'
import {
  SessionContext,
  INITIAL_PRACTITIONER,
  INITIAL_SHARED,
} from './sessionContextState'
import type { PractitionerSetup, SharedSetup, UserRole, WotSelection } from './sessionTypes'
import { writePersistedSessionStartOptions } from '@/utils/sessionStartOptionsStorage'

function persistSessionStartOptions(practitioner: PractitionerSetup) {
  writePersistedSessionStartOptions({
    includeWotCheck: practitioner.includeWotCheck,
    includeWarmup: practitioner.includeWarmup,
    moodCheckMode: practitioner.moodCheckMode,
  })
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [practitioner, setPractitionerState] = useState<PractitionerSetup>(INITIAL_PRACTITIONER)

  const [shared, setSharedState] = useState<SharedSetup>(INITIAL_SHARED)

  const [clientName,         setClientName]         = useState<string>('')
  const [panasScore,         setPanasScore]         = useState<number | null>(null)
  const [wotSelection,       setWotSelection]       = useState<WotSelection | null>(null)
  const [clientWotSelection, setClientWotSelection] = useState<WotSelection | null>(null)
  const [clientPanasScore,     setClientPanasScore]     = useState<number | null>(null)
  const [clientPostPanasScore, setClientPostPanasScore] = useState<number | null>(null)
  const [postSessionPanasSkipped, setPostSessionPanasSkipped] = useState(false)
  const [sessionNotes,         setSessionNotes]         = useState<string>('')
  const [followUpNotes,        setFollowUpNotes]        = useState<string>('')
  const [userRole,             setUserRole]             = useState<UserRole>(null)

  const setPractitioner = useCallback((update: Partial<PractitionerSetup>) => {
    setPractitionerState((prev) => {
      const next = { ...prev, ...update }
      if (
        'includeWotCheck' in update ||
        'includeWarmup' in update ||
        'moodCheckMode' in update
      ) {
        persistSessionStartOptions(next)
      }
      return next
    })
  }, [])

  const setShared = useCallback(
    (update: Partial<SharedSetup>) => setSharedState((prev) => ({ ...prev, ...update })),
    [],
  )

  const clearSession = () => {
    setPractitionerState(INITIAL_PRACTITIONER)
    setSharedState(INITIAL_SHARED)
    setClientName('')
    setPanasScore(null)
    setWotSelection(null)
    setClientWotSelection(null)
    setClientPanasScore(null)
    setClientPostPanasScore(null)
    setPostSessionPanasSkipped(false)
    setSessionNotes('')
    setFollowUpNotes('')
    setUserRole(null)
  }

  const isPractitionerReady =
    practitioner.relationshipType !== null && practitioner.sessionMode !== null

  return (
    <SessionContext.Provider
      value={{ practitioner, shared, clientName, panasScore, wotSelection, clientWotSelection, clientPanasScore, clientPostPanasScore, postSessionPanasSkipped, sessionNotes, followUpNotes, userRole, setPractitioner, setShared, setClientName, setPanasScore, setWotSelection, setClientWotSelection, setClientPanasScore, setClientPostPanasScore, setPostSessionPanasSkipped, setSessionNotes, setFollowUpNotes, setUserRole, clearSession, isPractitionerReady }}
    >
      {children}
    </SessionContext.Provider>
  )
}
